// Regra 1 (2026-07-28) — classificador PURO do motivo pelo qual a KEK não abre.
//
// Mora sozinho, sem NENHUM import, por dois motivos:
//   1. é lógica pura (string → união), não tem nada que ver com o armazenamento
//      da KEK;
//   2. `kek-member-session` é mockado em vários testes de tela; se o classificador
//      vivesse lá, todo mock o apagaria e o catch de produção chamaria `undefined`
//      — falha silenciosa exatamente no caminho que este trabalho existe para
//      tornar honesto. Aqui ele é imune a mock.

/**
 * Os motivos pelos quais a KEK não abre NESTA sessão. Significam a mesma coisa para
 * o membro ("entre novamente"), mas são classificados separadamente de propósito: um
 * predicado agregado (ex.: prefixo 'kek-member-session:') ficaria verde com metade da
 * classificação removida.
 *
 * ⚠️ OS DOIS NÃO TÊM O MESMO STATUS (verificado em 2026-07-28):
 *
 * - `no_kek_in_session` é o **caminho vivo** — é o que acontece no F5, no descarte
 *   de aba pelo Chrome, ao duplicar/restaurar aba. É o bug reproduzido em produção.
 * - `kek_decrypt_failed` é **branch defensivo**, não alcançável pela API pública:
 *   `loadKekMembro` checa `if (!sessionKey) throw no_kek_in_session` ANTES de tentar
 *   decifrar, e `sessionKey` só existe se `storeKekMembro` rodou NESTE realm — ato
 *   que grava um ciphertext casado com ela. Num realm só não há como ter chave
 *   válida e ciphertext incompatível. Só se alcança com o ciphertext corrompido por
 *   fora do fluxo normal.
 *
 * 🔓 CONSEQUÊNCIA — SEAM ABERTO, DE PROPÓSITO E REGISTRADO: por não ser alcançável,
 * o literal de `kek_decrypt_failed` NÃO tem teste de contrato (o de
 * `no_kek_in_session` tem — `kek-session-contrato.test.ts` alimenta a exceção REAL
 * no classificador). Ele segue pinado À MÃO nos dois lados: renomear a mensagem no
 * `loadKekMembro` deixaria este ramo mudo sem nenhum teste vermelho. Fechar exige
 * erro tipado no throw site (mexe em código de sessão) — decisão adiada pelo
 * operador em 28/07, não esquecimento.
 */
export type KekSessionReason = 'no_kek_in_session' | 'kek_decrypt_failed';

/**
 * Classifica uma exceção do carregamento da KEK. `null` = NÃO é problema de sessão
 * (falha de cripto de verdade — a borda honesta `falha_decrypt` continua existindo
 * e não pode ser engolida por esta função).
 */
export function kekSessionReason(e: unknown): KekSessionReason | null {
  const msg = e instanceof Error ? e.message : '';
  if (msg.includes('no_kek_in_session')) return 'no_kek_in_session';
  if (msg.includes('kek_decrypt_failed')) return 'kek_decrypt_failed';
  return null;
}

/**
 * ⚠️ AVISO ANTECIPADO — **NÃO é gate**. Leia antes de "endurecer" isto.
 *
 * Quem DECIDE se a escrita acontece é o `withCaseDek` no submit, e ele já falha
 * FECHADO: sem KEK, o unwrap da privada lança e o membro recebe erro. A sonda só
 * ANTECIPA a descoberta para o mount, para o membro não redigir uma fundamentação
 * inteira e só então descobrir que precisa reautenticar.
 *
 * Por isso bloqueia SÓ com determinação POSITIVA (`no_kek_in_session` ou
 * `kek_decrypt_failed`). Qualquer outro resultado — inclusive EXCEÇÃO — NÃO
 * bloqueia. Fail-closed aqui não fecharia nada que o submit não feche: criaria um
 * caminho de NEGAÇÃO NOVO, em que um bug na sonda impede um membro de escrever
 * parecer, sem ganho de segurança nenhum.
 *
 * **Fail-closed é regra para GATE. Isto não é gate.**
 *
 * Recebe a sonda por PARÂMETRO para que a regra seja testável por si (a exceção é
 * injetável); `kekMortaNestaSessao` é o wiring com a sonda real. Contrato: NUNCA lança.
 */
export async function bloqueiaPorSonda(
  sonda: () => Promise<KekSessionReason | null>,
): Promise<boolean> {
  try {
    return (await sonda()) !== null;
  } catch (e) {
    // OBSERVÁVEL, mas não bloqueante. Sem este rastro um bug na sonda ficaria
    // invisível para sempre — engolido por desenho. Logamos SÓ um marcador fixo + o
    // NOME da exceção (nunca message/stack: não deve carregar dado do caso, mas o
    // custo de garantir é zero). Falha de sonda não tem PII de denunciante, e a
    // regra dura do projeto veda IP/User-Agent/Referer/cf.* — nada disso aqui.
    console.warn('[kek-sonda] falha inesperada; NAO bloqueando a escrita', {
      name: e instanceof Error ? e.name : typeof e,
    });
    return false; // exceção NÃO é determinação positiva → não bloqueia.
  }
}
